import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary, BtnDestructive } from "@/pages/Senders";
import { Key, Upload, TestTube, Trash, CheckCircle, Warning, Circle } from "@phosphor-icons/react";

/**
 * OAuth Projects — pool of Google Cloud OAuth clients we use to connect
 * Gmail seed mailboxes. Google caps external "testing" apps at 100 test
 * users per client so we pool multiple and load-balance new connections.
 *
 * Bulk import: drop N `client_secret_*.json` files Google Cloud downloads;
 * backend parses `web` / `installed` block, encrypts secret, inserts.
 */
export default function OAuthProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testingAll, setTestingAll] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/oauth-projects");
      setProjects(data.projects || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImporting(true);
    try {
      const projectsPayload = await Promise.all(files.map(async (f) => ({
        file_name: f.name,
        contents: await f.text(),
      })));
      const { data } = await api.post("/admin/oauth-projects/bulk-import", {
        projects: projectsPayload,
        max_test_users: 100,
      });
      const created = data.created || 0;
      const skipped = data.skipped || 0;
      const errs = data.error_count || 0;
      toast.success(`Imported ${created}, skipped ${skipped}${errs ? `, ${errs} error(s)` : ""}`);
      if (data.errors?.length) {
        console.warn("bulk-import errors", data.errors);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const testOne = async (id) => {
    setTestingId(id);
    try {
      const { data } = await api.post(`/admin/oauth-projects/${id}/test`);
      toast[data.ok ? "success" : "error"](data.message);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTestingId(null);
    }
  };

  const testAll = async () => {
    if (!projects.length) return;
    setTestingAll(true);
    try {
      const { data } = await api.post("/admin/oauth-projects/test-all");
      toast.success(`Tested ${data.tested} · ${data.ok} ok · ${data.broken} broken`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTestingAll(false); }
  };

  const remove = async (id, name, slotsUsed) => {
    if (slotsUsed > 0) {
      toast.error(`Cannot delete — ${slotsUsed} mailbox(es) attached`);
      return;
    }
    if (!confirm(`Delete OAuth project "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/oauth-projects/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const totalFree = projects.reduce((n, p) => n + (p.slots_free || 0), 0);
  const totalUsed = projects.reduce((n, p) => n + (p.slots_used || 0), 0);

  return (
    <div className="p-8 max-w-6xl" data-testid="oauth-projects-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-mono uppercase tracking-wider flex items-center gap-2">
            <Key size={22} weight="bold" className="text-[#E1FF00]" /> OAuth Projects
          </h1>
          <p className="text-xs text-[#71717A] mt-1 font-mono max-w-2xl">
            Pool of Google Cloud OAuth clients used to connect seed Gmail mailboxes.
            Each Google testing app is capped at 100 test users — pool multiple to scale.
          </p>
        </div>
        <div className="flex gap-2">
          <input type="file" multiple accept=".json,application/json"
            ref={fileRef} onChange={onFilesPicked} className="hidden"
            data-testid="oauth-projects-bulk-file" />
          <BtnPrimary onClick={() => fileRef.current?.click()} disabled={importing} testid="oauth-projects-bulk-btn">
            <Upload size={13} weight="bold" /> {importing ? "Importing..." : "Bulk import JSONs"}
          </BtnPrimary>
          <BtnSecondary onClick={testAll} disabled={testingAll || !projects.length} testid="oauth-projects-test-all">
            <TestTube size={13} weight="bold" /> {testingAll ? "Testing..." : "Test all"}
          </BtnSecondary>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <StatTile label="Projects" value={projects.length} />
        <StatTile label="Slots used" value={totalUsed} />
        <StatTile label="Free slots" value={totalFree} accent="#E1FF00" />
      </div>

      <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
        <table className="dtable w-full" data-testid="oauth-projects-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Client ID</th>
              <th>Used / Cap</th>
              <th>Health</th>
              <th>Added</th>
              <th style={{ width: 200 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center text-[#71717A] py-8 font-mono">[ loading... ]</td></tr>}
            {!loading && projects.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[#71717A] py-8 font-mono">
                [ no projects yet — click "Bulk import JSONs" ]
              </td></tr>
            )}
            {projects.map((p) => (
              <tr key={p.id}>
                <td className="font-mono text-xs">
                  <div>{p.name}</div>
                  {p.gcp_project_id && <div className="text-[10px] text-[#71717A]">{p.gcp_project_id}</div>}
                </td>
                <td className="font-mono text-[10px] text-[#71717A] max-w-xs truncate">{p.client_id}</td>
                <td className="font-mono text-xs">
                  <span className={p.slots_free === 0 ? "text-[#FF3B30]" : ""}>
                    {p.slots_used} / {p.max_test_users}
                  </span>
                </td>
                <td>
                  <HealthPill ok={p.last_test_ok} message={p.last_test_message} testedAt={p.last_tested_at} />
                </td>
                <td className="font-mono text-[10px] text-[#71717A]">{(p.created_at || "").slice(0, 10)}</td>
                <td>
                  <div className="flex gap-1.5">
                    <BtnSecondary onClick={() => testOne(p.id)} testid={`oauth-project-test-${p.id}`}>
                      <TestTube size={11} weight="bold" /> {testingId === p.id ? "..." : "Test"}
                    </BtnSecondary>
                    <BtnDestructive onClick={() => remove(p.id, p.name, p.slots_used)}
                      disabled={p.slots_used > 0} testid={`oauth-project-delete-${p.id}`}>
                      <Trash size={11} weight="bold" />
                    </BtnDestructive>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 text-[11px] text-[#71717A] font-mono border border-[#2A2A2A] bg-[#0A0A0A] p-4 leading-relaxed">
        <div className="uppercase text-white mb-2">Google Cloud setup checklist</div>
        <div>1. Create N Google Cloud projects (one per 100 mailboxes you plan to connect)</div>
        <div>2. Each project: <span className="text-white">APIs & Services → Library → Enable Gmail API</span></div>
        <div>3. <span className="text-white">OAuth consent screen</span> → External, scopes: <span className="text-[#00E5FF]">openid profile email gmail.modify</span>, add every seed email under <span className="text-white">Test users</span></div>
        <div>4. <span className="text-white">Credentials</span> → Create OAuth Client → Web application → Authorized redirect URI = <span className="text-[#E1FF00]">https://cold.vu/api/oauth/gmail/callback</span></div>
        <div>5. Download the JSON, drop into "Bulk import JSONs" above</div>
      </div>
    </div>
  );
}

function StatTile({ label, value, accent = "#00E5FF" }) {
  return (
    <div className="border border-[#2A2A2A] bg-[#0A0A0A] p-4">
      <div className="text-[10px] uppercase tracking-wider text-[#71717A] font-mono">{label}</div>
      <div className="text-2xl font-mono mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function HealthPill({ ok, message, testedAt }) {
  if (ok === true) {
    return <span title={message || ""} className="inline-flex items-center gap-1 text-[11px] font-mono text-[#00E5FF]">
      <CheckCircle size={11} weight="fill" /> ok
    </span>;
  }
  if (ok === false) {
    return <span title={message || ""} className="inline-flex items-center gap-1 text-[11px] font-mono text-[#FF3B30]">
      <Warning size={11} weight="fill" /> broken
    </span>;
  }
  return <span title={testedAt || "not tested"} className="inline-flex items-center gap-1 text-[11px] font-mono text-[#71717A]">
    <Circle size={9} weight="fill" /> untested
  </span>;
}
